import styles from './use-components-mobile-view.module.scss';

import clsx from 'clsx';
import React, {
  DetailedHTMLProps,
  HTMLAttributes,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { ReactComponent as DragIndicatorIcon } from '@work4all/assets/icons/drag-vertical.svg';

import { BOMComponent } from '@work4all/models/lib/Classes/BOMComponent.entity';

import { useEditTable } from '../../../../erp/components/tab-panels/positions/components/edit-table/components/edit-table-provider/EditTableProvider';
import { useEditTableDrag } from '../../../../erp/components/tab-panels/positions/components/edit-table/components/TableRow/use-edit-table-drag';
import { useEditTableAllCells } from '../../../../erp/components/tab-panels/positions/components/edit-table/hooks/use-edit-table-all-cells';
import { useEditableRow } from '../../../../erp/components/tab-panels/positions/components/edit-table/hooks/use-editable-row';
import { InfiniteRow } from '../../../../erp/components/tab-panels/positions/components/edit-table/InfiniteList';
import { EditTableProps } from '../../../../erp/components/tab-panels/positions/components/edit-table/types';

type EntryBOMComponent = BOMComponent & { localId?: string };

export const useComponentsMobileViews = (
  props: UseComponentsMobileViewProps
) => {
  const renderMobilePosition = useCallback(
    (
      position: EntryBOMComponent,
      setEditedPosition: (position: EntryBOMComponent) => void,
      allPositions: EntryBOMComponent[]
    ) => {
      return (
        <ArticleMobileTable
          key={position.id}
          position={position}
          setEditedPosition={setEditedPosition}
          allPositions={allPositions}
          {...props}
        />
      );
    },
    []
  );

  const classes = useMemo(
    () => ({
      mobile: styles.mobilePositionsTable,
    }),
    []
  );
  return { renderMobilePosition, classes };
};

const wrapWithRow = (position: EntryBOMComponent) => {
  return {
    id: position.localId,
    index: position.index,
    original: position,
  } as unknown as InfiniteRow<EntryBOMComponent>;
};

interface ArticleMobileTableProps extends UseComponentsMobileViewProps {
  position: EntryBOMComponent;
  setEditedPosition: (position: EntryBOMComponent) => void;
  allPositions: EntryBOMComponent[];
}

interface UseComponentsMobileViewProps {
  columns?: EditTableProps<EntryBOMComponent>['columns'];
}

const DEFAULT_EDITABLE_ROWS = {};

export const ArticleMobileTable = (props: ArticleMobileTableProps) => {
  const { position, setEditedPosition, allPositions, columns } = props;

  const allCells = useEditTableAllCells<EntryBOMComponent>(columns);

  const data = useRef(allPositions);
  data.current = allPositions;

  const { onMovePosition } = useEditTable();

  const { calculateRowProperties } = useEditableRow({
    allCells,
    data,
    editableRows: DEFAULT_EDITABLE_ROWS,
    onMovePosition,
  });

  const { canDrop, handleMoveRow } = calculateRowProperties(
    wrapWithRow(position),
    allPositions.map(wrapWithRow)
  );

  const { direction, isDragging, isOver, dropRef, dragRef } = useEditTableDrag<
    HTMLTableRowElement,
    HTMLTableCellElement
  >({
    canDrop,
    index: allPositions.findIndex((x) => x.localId === position.localId),
    localId: position.localId,
    moveRowDone: handleMoveRow,
  });

  const dragCell = (
    <td ref={dragRef}>
      <DragIndicatorIcon />
    </td>
  );

  const dragStyle: React.CSSProperties = {
    opacity: isDragging ? 0.2 : 1,
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };

  const rowProps: DetailedHTMLProps<
    HTMLAttributes<HTMLTableRowElement>,
    HTMLTableRowElement
  > = {
    style: dragStyle,
    className: isDragging ? styles.noselect : undefined,
    onContextMenu: (e) => e.preventDefault(),
  };

  return (
    <React.Fragment key={position.id}>
      {isOver && direction === 'up' ? (
        <tr className={styles['highlight-row']}>
          <td colSpan={4} />
        </tr>
      ) : undefined}
      <tr
        ref={dropRef}
        className={clsx(styles.mobilePositionRow, rowProps.className)}
        style={rowProps.style}
        onContextMenu={rowProps.onContextMenu}
      >
        {dragCell}
        <td
          style={{ width: '100%' }}
          onClick={() => {
            setEditedPosition(position);
          }}
        >
          <div style={{ whiteSpace: 'pre-line' }}>{position.name}</div>

          <div className={styles.subline}>
            <div className={styles.left}>
              <span>
                {position.amount} {position.article?.unit?.name}
              </span>
              <span>{position.article?.articleKind}</span>
              <span> {position.article.number}</span>
            </div>
          </div>
        </td>
      </tr>
      {isOver && direction === 'down' ? (
        <tr className={styles['highlight-row']}>
          <td colSpan={2} />
        </tr>
      ) : undefined}
    </React.Fragment>
  );
};
